import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Site-wide chat widget injector.
 *
 * Fetches the raw snippet stored under `chat_widget_snippet` in site settings and
 * injects it into <body>. Because most chat providers ship inline <script> tags
 * that browsers won't execute when set via innerHTML, we walk the parsed HTML,
 * clone every <script> into a real script element, and append it manually — this
 * mirrors how a normal page load would evaluate the snippet.
 *
 * Rendered once from <App />, so all routes get the widget for free.
 */
const ChatWidget = () => {
  const injectedRef = useRef(false);
  const { pathname } = useLocation();
  const protectedPage = pathname.startsWith('/portal') || pathname.startsWith('/admin') || pathname === '/checkout' || pathname === '/quote/success';

  useEffect(() => {
    document.body.classList.toggle('kyis-hide-chat', protectedPage);
    return () => document.body.classList.remove('kyis-hide-chat');
  }, [protectedPage]);

  useEffect(() => {
    if (injectedRef.current) return;

    let cancelled = false;
    const injectedNodes = [];

    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/chat-widget`);
        const snippet = (res?.data?.snippet || '').trim();
        if (!snippet || cancelled) return;

        const container = document.createElement('div');
        container.setAttribute('data-kyis-chat-widget', '1');
        container.style.display = 'contents';
        container.innerHTML = snippet;

        // Re-create <script> tags so the browser actually executes them.
        Array.from(container.querySelectorAll('script')).forEach((oldScript) => {
          const newScript = document.createElement('script');
          for (const attr of oldScript.attributes) {
            newScript.setAttribute(attr.name, attr.value);
          }
          newScript.text = oldScript.textContent || '';
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });

        document.body.appendChild(container);
        injectedNodes.push(container);
        injectedRef.current = true;
      } catch {
        // Chat widget is optional — silent failure is fine.
      }
    })();

    return () => {
      cancelled = true;
      injectedNodes.forEach((n) => {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    };
  }, []);

  return <style>{`body.kyis-hide-chat #lc_text-widget,
    body.kyis-hide-chat [data-kyis-chat-widget],
    body.kyis-hide-chat chat-widget,
    body.kyis-hide-chat iframe[title*="chat" i] { display: none !important; }`}</style>;
};

export default ChatWidget;
